import { User } from '../types';
import { apiService } from './apiService';
import { API_ENDPOINTS } from '../config/api.config';

interface Auth0User {
    __raw: string;
    email: string;
}

interface AuthResponse {
    user: User;
    token: string;
    refreshToken: string;
}

interface PasswordStatusResponse {
    force_password_change: boolean;
}

class AuthService {
    private currentUser: User | null = null;

    public async handleLoginCallback(auth0User: Auth0User): Promise<void> {
        try {
            const response = await apiService.post<AuthResponse>(API_ENDPOINTS.AUTH.AUTH0.VERIFY_TOKEN, {
                auth0Token: auth0User.__raw,
                email: auth0User.email
            });

            if (response) {
                this.currentUser = response.user;
                localStorage.setItem('user', JSON.stringify(response.user));
                localStorage.setItem('access_token', response.token);
                localStorage.setItem('refresh_token', response.refreshToken);

                if (response.user.id) {
                    localStorage.setItem('userId', response.user.id.toString());
                    localStorage.setItem('organizationId', response.user.organizationId);
                }
            }
        } catch (error) {
            console.error('Login callback error:', error);
            throw error;
        }
    }

    public getUser(): User | null {
        if (!this.currentUser) {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                try {
                    this.currentUser = JSON.parse(userStr);

                    // Ensure userId is set in localStorage if we have a user
                    if (this.currentUser && this.currentUser.id && !localStorage.getItem('userId')) {
                        console.log('Fixing missing userId in localStorage');
                        localStorage.setItem('userId', this.currentUser.id.toString());
                    }
                } catch (error) {
                    console.error('Error parsing user from localStorage:', error);
                }
            }
        }
        return this.currentUser;
    }

    public isAuthenticated(): boolean {
        const user = this.getUser();
        // Check for either access_token or Auth0 token
        const hasToken = !!localStorage.getItem('access_token') || !!localStorage.getItem('auth0_access_token');
        return !!user && hasToken;
    }

    public getAuthProvider(): string {
        return 'auth0';
    }

    public async checkPasswordStatus(): Promise<PasswordStatusResponse> {
        try {
            const response = await apiService.get<PasswordStatusResponse>(API_ENDPOINTS.AUTH.CHECK_PASSWORD_STATUS);
            return response || { force_password_change: false };
        } catch (error) {
            console.error('Error checking password status:', error);
            return { force_password_change: false };
        }
    }

    public clearAuth(): void {
        this.currentUser = null;
        localStorage.removeItem('access_token');
        localStorage.removeItem('auth0_access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        localStorage.removeItem('organizationId');
    }

    public async logout(): Promise<void> {
        try {
            await apiService.post(API_ENDPOINTS.AUTH.LOGOUT, {});
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            this.clearAuth(); // Always clear auth
        }
    }
}

export const authService = new AuthService();